/**
 * Social Scheduler Service
 * Handles API calls for social media scheduling via Metricool integration.
 * Available only for Ultra tier users.
 */

import { getAccessToken } from '../../shared/utils/secureTokenStorage';

const API_BASE = process.env.REACT_APP_API_BASE || '';

class SocialSchedulerService {
  /**
   * Get authentication headers
   */
  getHeaders() {
    const token = getAccessToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Handle API response
   */
  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || 'Request failed');
    }
    return response.json();
  }

  /**
   * Get social scheduler configuration
   * Returns available platforms, credit costs, and user's brand info
   */
  async getConfig() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/config`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Assign a brand slot to the current user
   * This creates the user's Metricool brand for social account connections
   */
  async assignBrand() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/brands/assign`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get connection URL for linking social accounts
   * Returns an iframe URL for Metricool's OAuth flow
   */
  async getConnectionUrl(returnUrl = null) {
    const body = returnUrl ? { returnUrl } : {};
    const response = await fetch(`${API_BASE}/api/social-scheduler/connect`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse(response);
  }

  /**
   * Create a new scheduled post
   * @param {Object} postData - Post data
   * @param {string} postData.content - Post text/caption (1-2200 chars)
   * @param {string[]} postData.platforms - Target platforms (instagram, facebook, linkedin, etc.)
   * @param {string} postData.scheduledTime - ISO datetime (e.g., 2026-05-10T10:00:00Z)
   * @param {string} postData.timezone - Timezone (default: America/Chicago)
   * @param {string[]} postData.mediaUrls - Optional media URLs (images/videos)
   * @param {string} postData.firstComment - Optional first comment (Instagram)
   * @param {boolean} postData.isDraft - Save as draft instead of scheduling
   */
  async createPost(postData) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/posts`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(postData),
    });
    return this.handleResponse(response);
  }

  /**
   * Get list of scheduled posts
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status (draft, scheduled, published, failed, cancelled)
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   */
  async listPosts({ status = null, page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);

    const response = await fetch(`${API_BASE}/api/social-scheduler/posts?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get a single post by ID
   * @param {string} postId - Post ID
   */
  async getPost(postId) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/posts/${postId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Update a scheduled post
   * @param {string} postId - Post ID
   * @param {Object} updates - Fields to update
   * @param {string} updates.content - New content
   * @param {string} updates.scheduledTime - New scheduled time
   * @param {string} updates.timezone - New timezone
   */
  async updatePost(postId, updates) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/posts/${postId}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  /**
   * Cancel a scheduled post (refunds credits)
   * @param {string} postId - Post ID
   */
  async cancelPost(postId) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/posts/${postId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get calendar view of posts
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   */
  async getCalendar(year, month) {
    const params = new URLSearchParams({ year: String(year), month: String(month) });
    const response = await fetch(`${API_BASE}/api/social-scheduler/calendar?${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get analytics for a platform
   * @param {string} platform - Platform name
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  async getAnalytics(platform, startDate, endDate) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/analytics`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ platform, startDate, endDate }),
    });
    return this.handleResponse(response);
  }

  /**
   * Get best times to post for a platform
   * @param {string} platform - Platform name
   */
  async getBestTimes(platform) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/best-times/${platform}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Refresh connected accounts from Metricool
   * Updates the local cache of connected social accounts
   */
  async refreshAccounts() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/accounts/refresh`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get analytics summary across all platforms
   * Returns aggregate metrics for the last 30 days
   */
  async getAnalyticsSummary() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/analytics/summary`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get detailed analytics for a platform
   * @param {string} platform - Platform name (instagram, facebook, etc.)
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  async getPlatformAnalytics(platform, startDate, endDate) {
    const params = new URLSearchParams({
      platform,
      startDate,
      endDate
    });
    const response = await fetch(`${API_BASE}/api/social-scheduler/analytics?${params}`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List all connected social accounts
   */
  async listAccounts() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/accounts`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Disconnect a social account
   * @param {string} accountId - Account ID to disconnect
   */
  async disconnectAccount(accountId) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/accounts/${accountId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Complete the OAuth connection flow
   * Called after user completes the connection iframe
   */
  async completeConnection() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/connect/complete`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // =============================================================================
  // Admin Endpoints (for admin users only)
  // =============================================================================

  /**
   * Get brand statistics (admin only)
   */
  async getBrandStats() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/brand-stats`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List all brands with user assignments (admin only)
   * @param {string} status - Optional status filter (available, assigned, pending, suspended)
   */
  async listAllBrands(status = null) {
    const params = status ? `?status=${status}` : '';
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/brands${params}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get detailed brand info (admin only)
   * @param {string} brandId - Brand ID
   */
  async getBrandDetails(brandId) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/brands/${brandId}`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Release a brand slot (admin only)
   * @param {string} brandId - Brand ID to release
   */
  async releaseBrand(brandId) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/brands/${brandId}/release`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get brand for a specific user (admin only)
   * @param {string} userEmail - User email to look up
   */
  async getUserBrand(userEmail) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/user/${encodeURIComponent(userEmail)}/brand`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List all Metricool brands (admin only)
   * Used to get blog IDs for initialization
   */
  async listMetricoolBrands() {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/metricool-brands`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Initialize brand slots (admin only)
   * @param {string[]} metricoolBlogIds - List of Metricool blog IDs
   * @param {boolean} force - Force update existing brands
   */
  async initializeBrands(metricoolBlogIds, force = false) {
    const response = await fetch(`${API_BASE}/api/social-scheduler/admin/initialize-brands`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        metricoolBlogIds,
        force,
      }),
    });
    return this.handleResponse(response);
  }
}

// Export singleton instance
export const socialSchedulerService = new SocialSchedulerService();
export default socialSchedulerService;
